/**
 * API client for Tenants endpoints
 */

import apiClient from "./client";
import type { Tenant, TenantBranding, UpdateTenantRequest } from "@/types/tenant";
import type {
  StripeConfig,
  StripeConfigRequest,
  StripeVerifyResponse,
} from "@/types/stripeConfig";

/**
 * Public tenant info (for unauthenticated requests)
 */
export interface PublicTenant {
  tenant_id: string;
  name: string;
  logo_url?: string | null;
  primary_color?: string;
  secondary_color?: string;
}

/**
 * Tenants API endpoints
 */
export const tenantsApi = {
  /**
   * Get current tenant information
   */
  getCurrent: () => apiClient.get<Tenant>("/api/tenants/v1/"),

  /**
   * Get tenant by ID
   */
  get: (tenantId: string) =>
    apiClient.get<Tenant>(`/api/tenants/v1/${tenantId}`),

  /**
   * Update tenant settings
   */
  update: (_tenantId: string, data: UpdateTenantRequest) =>
    apiClient.patch<Tenant>("/api/tenants/v1/", data),

  /**
   * Get public tenant info (no authentication required)
   */
  getPublic: (tenantId: string) =>
    apiClient.get<PublicTenant>("/api/tenants/v1/public", {
      params: { tenant_id: tenantId },
    }),

  /**
   * Get public tenant branding by slug (no authentication required)
   */
  getPublicBranding: (slug: string) =>
    apiClient.get<TenantBranding>("/api/tenants/v1/public/branding", {
      params: { slug },
    }),

  // ========================================================================
  // Stripe Configuration
  // ========================================================================

  /**
   * Get Stripe configuration status for current tenant
   */
  getStripeConfig: () =>
    apiClient.get<StripeConfig>("/api/tenants/v1/stripe-config"),

  /**
   * Create or update Stripe configuration
   */
  updateStripeConfig: (data: StripeConfigRequest) =>
    apiClient.post<StripeConfig>("/api/tenants/v1/stripe-config", data),

  /**
   * Verify Stripe configuration works
   */
  verifyStripeConfig: () =>
    apiClient.post<StripeVerifyResponse>("/api/tenants/v1/stripe-config/verify"),

  /**
   * Deactivate Stripe configuration
   */
  deleteStripeConfig: () =>
    apiClient.delete<{ message: string }>("/api/tenants/v1/stripe-config"),

  /**
   * Get the tenant's Stripe publishable key (for loading Stripe.js)
   */
  getStripePublishableKey: () =>
    apiClient.get<{ publishable_key: string }>(
      "/api/tenants/v1/stripe-config/publishable-key"
    ),
};

export default tenantsApi;
